/**
 * Spend Summary Card
 * Shows current month total with comparison to previous month
 */

import { ArrowUp, ArrowDown, Minus, DollarSign } from 'lucide-react';
import type { SpendSummary } from '../types';

interface SpendSummaryCardProps {
  summary: SpendSummary;
  currency: string;
}

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

export function SpendSummaryCard({ summary, currency }: SpendSummaryCardProps) {
  const { currentMonth, previousMonth, percentChange, projectedTotal } = summary;
  const isIncrease = percentChange > 0;
  const isDecrease = percentChange < 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Current Month */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
          <DollarSign className="h-4 w-4" />
          <span>Current Month</span>
        </div>
        <div className="text-3xl font-bold text-gray-900">
          {formatCurrency(currentMonth.total, currency)}
        </div>
        <div className="text-sm text-gray-500 mt-1">
          {formatDate(currentMonth.startDate)} - {formatDate(currentMonth.endDate)}
        </div>
        <div className="flex items-center gap-1 mt-3">
          {isIncrease && <ArrowUp className="h-4 w-4 text-red-500" />}
          {isDecrease && <ArrowDown className="h-4 w-4 text-green-500" />}
          {!isIncrease && !isDecrease && <Minus className="h-4 w-4 text-gray-400" />}
          <span
            className={`text-sm font-medium ${
              isIncrease ? 'text-red-600' : isDecrease ? 'text-green-600' : 'text-gray-500'
            }`}
          >
            {Math.abs(percentChange).toFixed(1)}% vs last month
          </span>
        </div>
      </div>

      {/* Previous Month */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
          <DollarSign className="h-4 w-4" />
          <span>Previous Month</span>
        </div>
        <div className="text-3xl font-bold text-gray-900">
          {formatCurrency(previousMonth.total, currency)}
        </div>
        <div className="text-sm text-gray-500 mt-1">
          {formatDate(previousMonth.startDate)} - {formatDate(previousMonth.endDate)}
        </div>
      </div>

      {/* Projected Total */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
          <DollarSign className="h-4 w-4" />
          <span>Projected Month-End</span>
        </div>
        <div className="text-3xl font-bold text-blue-600">
          {formatCurrency(projectedTotal, currency)}
        </div>
        <div className="text-sm text-gray-500 mt-1">Based on current usage rate</div>
      </div>
    </div>
  );
}
